/**
 * Chunked cell world.
 *
 * The world is unbounded and sparse: chunks materialise on demand from a
 * generator callback. Each chunk keeps a dirty rectangle so the simulation only
 * touches cells that changed, and sleeps entirely after 8 quiet ticks.
 *
 * Coordinate conventions:
 *   - world cell coords are signed integers, +x right, +y DOWN
 *   - chunk coords = cell >> CHUNK_SHIFT (arithmetic shift, so negatives work)
 *
 * Performance notes:
 *   - `get`/`set` go through a single-entry chunk cache. Sequential scans (the
 *     overwhelmingly common access pattern) hit it every time.
 *   - Nothing in this file allocates during steady-state play. That claim is
 *     only worth something if the systems built on top of it hold to it too —
 *     see the notes in `cellsim.ts:finish` and `render/terrain.ts:upload`.
 *
 * A chunk carries **three** dirty regions, not one, because three different
 * consumers need three different answers:
 *
 *   - the **movement rect** (`dirtyMin/Max`) — cells whose *matter* may move.
 *     Grown by `touch`, consumed by `cellsim`.
 *   - the **thermal rect** (`thermMin/Max`) — cells whose *temperature* may
 *     change. Grown by `touchTemp` *and* by `touch`, consumed by `thermal`.
 *     Conduction has to keep spreading through matter that is not moving, and
 *     paying for a movement pass over every conducting cell is what made
 *     thermal widen to whole chunks in the first place.
 *   - the **render tile mask** (`renderTiles`) — 16×16 tiles that changed since
 *     the last GPU upload. A bounding box was the wrong shape here: fire
 *     scattered across a chunk grew the box to the full 128×128 and uploaded
 *     16384 cells × 3 textures when a few hundred had changed.
 */

import { Flag, M, VOID, type MatId } from './materials';

export const CHUNK_SHIFT = 7;
export const CHUNK_SIZE = 1 << CHUNK_SHIFT; // 128
export const CHUNK_MASK = CHUNK_SIZE - 1;
export const CELLS_PER_CHUNK = CHUNK_SIZE * CHUNK_SIZE;

/** Ticks of quiet before a chunk sleeps. */
const SLEEP_AFTER = 8;

/**
 * Render-dirty granularity: 16×16-cell tiles, 8×8 of them per chunk.
 *
 * Chosen so the *upload* stays cheap in both directions. Finer tiles cut
 * bandwidth further but multiply `texSubImage2D` calls, and a state change costs
 * more than the bytes; coarser tiles degrade toward the bounding box this
 * replaces.
 */
export const RENDER_TILE_SHIFT = 4;
export const RENDER_TILE_SIZE = 1 << RENDER_TILE_SHIFT;
export const RENDER_TILE_AXIS_SHIFT = CHUNK_SHIFT - RENDER_TILE_SHIFT;
export const RENDER_TILES_PER_AXIS = 1 << RENDER_TILE_AXIS_SHIFT;
export const RENDER_TILE_COUNT = RENDER_TILES_PER_AXIS * RENDER_TILES_PER_AXIS;

export const Tier = { HOT: 0, WARM: 1, COLD: 2, FROZEN: 3 } as const;
export type TierId = (typeof Tier)[keyof typeof Tier];

export class Chunk {
  readonly cx: number;
  readonly cy: number;

  readonly mat: Uint16Array;
  readonly temp: Int16Array;
  readonly flags: Uint16Array;
  readonly aux: Uint16Array;

  /** Dirty rect in local coords; minX > maxX means empty. */
  dirtyMinX = 0;
  dirtyMinY = 0;
  dirtyMaxX = CHUNK_MASK;
  dirtyMaxY = CHUNK_MASK;

  /** Next tick's rect, accumulated during the current step. */
  nextMinX = CHUNK_SIZE;
  nextMinY = CHUNK_SIZE;
  nextMaxX = -1;
  nextMaxY = -1;

  /**
   * Thermal rect: cells whose temperature may still be changing. Superset of
   * the movement rect — `touch` grows both, `touchTemp` grows only this one.
   */
  thermMinX = 0;
  thermMinY = 0;
  thermMaxX = CHUNK_MASK;
  thermMaxY = CHUNK_MASK;
  thermNextMinX = CHUNK_SIZE;
  thermNextMinY = CHUNK_SIZE;
  thermNextMaxX = -1;
  thermNextMaxY = -1;

  /**
   * Bounding box of cells that were given `Flag.MOVED` this tick, so the
   * end-of-tick clear costs what the movement cost rather than sweeping every
   * active chunk's whole dirty rect. See `cellsim.ts:finish`.
   */
  movedMinX = CHUNK_SIZE;
  movedMinY = CHUNK_SIZE;
  movedMaxX = -1;
  movedMaxY = -1;
  /** Set while this chunk is queued in the cell sim's moved-chunk list. */
  movedListed = false;

  quietTicks = 0;
  asleep = false;
  tier: TierId = Tier.FROZEN;

  /**
   * Set when the cell sim ran out of budget before finishing this chunk. A
   * pending chunk must NOT rotate its dirty rect at end of tick — doing so
   * discards work that was deferred rather than completed, and the chunk then
   * goes quiet and sleeps with matter still mid-fall.
   */
  simPending = false;

  /** True if any cell here can move under gravity. Solid rock never needs a pass. */
  hasMobile = false;

  /** Tick at which this chunk was last simulated — drives frozen catch-up. */
  lastSimTick = 0;

  /** Set when cell contents changed since the last GPU upload. */
  renderDirty = true;
  /** One byte per 16×16 tile; 1 means "changed since the last upload". */
  readonly renderTiles = new Uint8Array(RENDER_TILE_COUNT);

  /** Counts, maintained incrementally to avoid full scans. */
  nonEmpty = 0;
  burning = 0;
  /**
   * Cells whose material seeds the light field (`M.lightSource`).
   *
   * Exists for the same reason `burning` does: the light field has to decide,
   * once per tick per visible chunk, whether this chunk needs a tile grid at
   * all. The alternative is reading every cell of every visible chunk every
   * tick to look for emitters, which is precisely the full-scan the dirty-rect
   * scheduler was built to avoid. Maintained by the same three writers that
   * maintain `nonEmpty`.
   */
  lightSources = 0;

  constructor(cx: number, cy: number) {
    this.cx = cx;
    this.cy = cy;
    this.mat = new Uint16Array(CELLS_PER_CHUNK);
    this.temp = new Int16Array(CELLS_PER_CHUNK);
    this.flags = new Uint16Array(CELLS_PER_CHUNK);
    this.aux = new Uint16Array(CELLS_PER_CHUNK);
    this.temp.fill(20);
    this.renderTiles.fill(1);
  }

  /**
   * Expand the movement *and* thermal next-tick rects to include a local cell,
   * dilated by 1, and mark its render tile.
   *
   * This is the hottest function in the simulation — every swap calls it twice.
   * Keep it branch-light.
   */
  touch(lx: number, ly: number): void {
    const a = lx - 1 < 0 ? 0 : lx - 1;
    const b = ly - 1 < 0 ? 0 : ly - 1;
    const c = lx + 1 > CHUNK_MASK ? CHUNK_MASK : lx + 1;
    const d = ly + 1 > CHUNK_MASK ? CHUNK_MASK : ly + 1;
    if (a < this.nextMinX) this.nextMinX = a;
    if (b < this.nextMinY) this.nextMinY = b;
    if (c > this.nextMaxX) this.nextMaxX = c;
    if (d > this.nextMaxY) this.nextMaxY = d;
    if (a < this.thermNextMinX) this.thermNextMinX = a;
    if (b < this.thermNextMinY) this.thermNextMinY = b;
    if (c > this.thermNextMaxX) this.thermNextMaxX = c;
    if (d > this.thermNextMaxY) this.thermNextMaxY = d;

    this.renderTiles[
      ((ly >> RENDER_TILE_SHIFT) << RENDER_TILE_AXIS_SHIFT) | (lx >> RENDER_TILE_SHIFT)
    ] = 1;
    this.renderDirty = true;

    this.asleep = false;
    this.quietTicks = 0;
  }

  /**
   * Temperature-only touch: keeps the cell in the thermal pass and schedules a
   * GPU upload, without dragging it into the movement pass.
   *
   * A block of rock heating toward ignition has to keep conducting, but nothing
   * in it is going to fall. Routing that through `touch` made every conducting
   * cell a cell-sim cell too, which is the other half of why fire was expensive.
   */
  touchTemp(lx: number, ly: number): void {
    const a = lx - 1 < 0 ? 0 : lx - 1;
    const b = ly - 1 < 0 ? 0 : ly - 1;
    const c = lx + 1 > CHUNK_MASK ? CHUNK_MASK : lx + 1;
    const d = ly + 1 > CHUNK_MASK ? CHUNK_MASK : ly + 1;
    if (a < this.thermNextMinX) this.thermNextMinX = a;
    if (b < this.thermNextMinY) this.thermNextMinY = b;
    if (c > this.thermNextMaxX) this.thermNextMaxX = c;
    if (d > this.thermNextMaxY) this.thermNextMaxY = d;

    this.renderTiles[
      ((ly >> RENDER_TILE_SHIFT) << RENDER_TILE_AXIS_SHIFT) | (lx >> RENDER_TILE_SHIFT)
    ] = 1;
    this.renderDirty = true;

    this.asleep = false;
    this.quietTicks = 0;
  }

  /** Mark the whole chunk dirty — used on generation and on wake. */
  touchAll(): void {
    this.nextMinX = 0;
    this.nextMinY = 0;
    this.nextMaxX = CHUNK_MASK;
    this.nextMaxY = CHUNK_MASK;
    this.thermNextMinX = 0;
    this.thermNextMinY = 0;
    this.thermNextMaxX = CHUNK_MASK;
    this.thermNextMaxY = CHUNK_MASK;
    this.markRenderAll();
    this.asleep = false;
    this.quietTicks = 0;
  }

  /** Force a full GPU re-upload — generation, and fresh GPU-side textures. */
  markRenderAll(): void {
    this.renderTiles.fill(1);
    this.renderDirty = true;
  }

  /** Promote the accumulated next-tick rects into the active ones. */
  rotateDirty(): void {
    if (this.simPending) return;
    const moveEmpty = this.nextMaxX < this.nextMinX;
    const thermEmpty = this.thermNextMaxX < this.thermNextMinX;

    if (moveEmpty) {
      this.dirtyMinX = 1;
      this.dirtyMaxX = 0;
    } else {
      this.dirtyMinX = this.nextMinX;
      this.dirtyMinY = this.nextMinY;
      this.dirtyMaxX = this.nextMaxX;
      this.dirtyMaxY = this.nextMaxY;
    }

    if (thermEmpty) {
      this.thermMinX = 1;
      this.thermMaxX = 0;
    } else {
      this.thermMinX = this.thermNextMinX;
      this.thermMinY = this.thermNextMinY;
      this.thermMaxX = this.thermNextMaxX;
      this.thermMaxY = this.thermNextMaxY;
    }

    // A chunk is only quiet when nothing moved *and* nothing is still
    // equalising temperature. Sleeping on the movement rect alone would freeze
    // a room mid-way through heating up.
    if (moveEmpty && thermEmpty) {
      this.quietTicks++;
      if (this.quietTicks >= SLEEP_AFTER) this.asleep = true;
    } else {
      this.quietTicks = 0;
    }

    this.nextMinX = CHUNK_SIZE;
    this.nextMinY = CHUNK_SIZE;
    this.nextMaxX = -1;
    this.nextMaxY = -1;
    this.thermNextMinX = CHUNK_SIZE;
    this.thermNextMinY = CHUNK_SIZE;
    this.thermNextMaxX = -1;
    this.thermNextMaxY = -1;
  }

  hasDirty(): boolean {
    return this.dirtyMaxX >= this.dirtyMinX;
  }

  hasThermDirty(): boolean {
    return this.thermMaxX >= this.thermMinX;
  }

  /** Clear the movement and thermal rects outright. Generation-time only. */
  clearDirty(): void {
    this.dirtyMinX = 1;
    this.dirtyMaxX = 0;
    this.thermMinX = 1;
    this.thermMaxX = 0;
  }

  clearRenderDirty(): void {
    this.renderDirty = false;
    this.renderTiles.fill(0);
  }
}

export type ChunkGenerator = (chunk: Chunk, cx: number, cy: number) => void;

/** Key packing for the chunk map. Supports ±32767 chunks per axis. */
function chunkKey(cx: number, cy: number): number {
  return ((cx + 32768) << 16) | (cy + 32768);
}

export class CellWorld {
  private chunks = new Map<number, Chunk>();
  /**
   * The same chunks as `chunks`, as a dense array.
   *
   * `updateActive` runs over every resident chunk every tick; iterating a Map
   * allocates an iterator and a result object per step, which is exactly the
   * per-tick garbage this file claims not to produce.
   */
  private resident: Chunk[] = [];
  private generator: ChunkGenerator;

  /** Single-entry lookup cache — sequential scans hit this ~100% of the time. */
  private cacheKey = -1;
  private cacheChunk: Chunk | null = null;

  /** Chunks that should be simulated this tick, rebuilt each tick. */
  readonly activeChunks: Chunk[] = [];

  tick = 0;

  /** Bumped whenever a cell changes; lets systems cheaply detect "any change". */
  revision = 0;

  constructor(generator: ChunkGenerator = defaultGenerator) {
    this.generator = generator;
  }

  setGenerator(g: ChunkGenerator): void {
    this.generator = g;
  }

  // ------------------------------------------------------------- chunks

  /** Get an existing chunk, or null. Never generates. */
  peekChunk(cx: number, cy: number): Chunk | null {
    return this.chunks.get(chunkKey(cx, cy)) ?? null;
  }

  /** Get a chunk, generating it if needed. */
  getChunk(cx: number, cy: number): Chunk {
    const key = chunkKey(cx, cy);
    let c = this.chunks.get(key);
    if (c === undefined) {
      c = new Chunk(cx, cy);
      this.chunks.set(key, c);
      this.generator(c, cx, cy);
      c.lastSimTick = this.tick;
      this.resident.push(c);
      this.recount(c);
      // Always upload freshly generated cells; but only schedule a simulation
      // pass if something in here can actually move. Most terrain chunks are
      // solid rock, and marking all of them fully dirty on generation floods
      // the cell sim's budget with hundreds of thousands of cells that were
      // never going to do anything — which starves the chunks that matter.
      c.markRenderAll();
      if (c.hasMobile) c.touchAll();
      else {
        // Uniform temperature at generation means no gradient, so the thermal
        // rect starts empty too. Handing thermal 16384 inert cells per
        // generated rock chunk would eat its whole quota for nothing.
        c.clearDirty();
        c.asleep = true;
      }
    }
    return c;
  }

  /** Chunk containing a world cell, generating on demand. */
  private chunkFor(x: number, y: number): Chunk {
    const cx = x >> CHUNK_SHIFT;
    const cy = y >> CHUNK_SHIFT;
    const key = chunkKey(cx, cy);
    if (key === this.cacheKey && this.cacheChunk !== null) return this.cacheChunk;
    const c = this.getChunk(cx, cy);
    this.cacheKey = key;
    this.cacheChunk = c;
    return c;
  }

  /**
   * Public form of `peekFor`, for systems that need direct array access to a
   * cell's chunk (the cell sim writes MOVED flags this way to avoid a full
   * set/touch round trip on every move).
   */
  peekChunkAt(x: number, y: number): Chunk | null {
    return this.peekFor(x, y);
  }

  /** Chunk containing a world cell, or null if not yet generated. */
  private peekFor(x: number, y: number): Chunk | null {
    const cx = x >> CHUNK_SHIFT;
    const cy = y >> CHUNK_SHIFT;
    const key = chunkKey(cx, cy);
    if (key === this.cacheKey) return this.cacheChunk;
    return this.chunks.get(key) ?? null;
  }

  get chunkCount(): number {
    return this.chunks.size;
  }

  allChunks(): IterableIterator<Chunk> {
    return this.chunks.values();
  }

  private recount(c: Chunk): void {
    let n = 0;
    let b = 0;
    let lit = 0;
    let mobile = false;
    for (let i = 0; i < CELLS_PER_CHUNK; i++) {
      const m = c.mat[i];
      if (m !== VOID) n++;
      if (c.flags[i] & Flag.BURNING) b++;
      if (M.lightSource[m] === 1) lit++;
      if (!mobile && M.mobile[m] === 1 && (c.flags[i] & Flag.STATIC) === 0) mobile = true;
    }
    c.nonEmpty = n;
    c.burning = b;
    c.lightSources = lit;
    c.hasMobile = mobile;
  }

  // -------------------------------------------------------- cell access

  get(x: number, y: number): MatId {
    const c = this.chunkFor(x, y);
    return c.mat[((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK)];
  }

  /** Read without generating. Returns VOID for ungenerated space. */
  peek(x: number, y: number): MatId {
    const c = this.peekFor(x, y);
    if (c === null) return VOID;
    return c.mat[((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK)];
  }

  getTemp(x: number, y: number): number {
    const c = this.chunkFor(x, y);
    return c.temp[((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK)];
  }

  getFlags(x: number, y: number): number {
    const c = this.chunkFor(x, y);
    return c.flags[((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK)];
  }

  getAux(x: number, y: number): number {
    const c = this.chunkFor(x, y);
    return c.aux[((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK)];
  }

  /**
   * Write a cell. `temp` defaults to the material's spawn temperature; pass
   * `keepTemp` to preserve whatever was there (used by transmutation, where the
   * heat that caused the change should carry through).
   */
  set(x: number, y: number, mat: MatId, temp?: number, aux?: number): void {
    const c = this.chunkFor(x, y);
    const lx = x & CHUNK_MASK;
    const ly = y & CHUNK_MASK;
    const i = (ly << CHUNK_SHIFT) | lx;

    const prev = c.mat[i];
    if (prev !== VOID) c.nonEmpty--;
    if (mat !== VOID) c.nonEmpty++;
    if (M.lightSource[prev] === 1) c.lightSources--;
    if (M.lightSource[mat] === 1) c.lightSources++;
    if (c.flags[i] & Flag.BURNING) c.burning--;

    c.mat[i] = mat;
    c.temp[i] = temp === undefined ? M.spawnTemp[mat] : clampTemp(temp);
    // Preserve only the stable per-cell variant bits; all state flags reset.
    c.flags[i] = c.flags[i] & Flag.VARIANT_MASK;
    c.aux[i] = aux === undefined ? initialAux(mat) : aux;

    c.touch(lx, ly);
    this.wakeNeighbours(c, lx, ly);
    this.revision++;
  }

  /**
   * Hook for relocating matter that has nowhere to go. Set by the game to the
   * particle layer; without it, `place` falls back to overwriting.
   */
  onEvict: ((x: number, y: number, mat: MatId, temp: number) => void) | null = null;

  /**
   * **Add** matter to a cell, displacing whatever is already there rather than
   * destroying it.
   *
   * This is the difference between `set` (authoritative write — worldgen,
   * transmutation, destruction) and placement (the player or the world adding
   * material to an occupied space). It matters more than it sounds:
   *
   * `docs/06-noita-lessons.md` §1 identified our worst design hole — liquids
   * seal routes without destroying terrain, and unlike Noita we cannot excuse a
   * soft-locked run with permadeath. Terraria's answer, adopted here, is that
   * *placing a block into a flooded tile evicts the liquid*. That gives the
   * player a general, always-available way to reclaim a drowned corridor,
   * without per-region authoring and without a special "unflood" ability.
   *
   * It also enforces the anti-jam invariant (`docs/11` §5.5): no code path
   * deletes a cell for want of space. The displaced material finds a
   * neighbour, or becomes a particle, or — only if the world is genuinely
   * packed solid — is refused.
   *
   * Returns true if the placement happened.
   */
  place(x: number, y: number, mat: MatId, temp?: number): boolean {
    const existing = this.peek(x, y);
    if (existing === VOID) {
      this.set(x, y, mat, temp);
      return true;
    }
    // Never displace structure. Placing into rock is a destruction question,
    // and destruction has its own gate (`damageCell`).
    if (M.solid[existing] === 1 || M.indestructible[existing] === 1) return false;

    const exTemp = this.getTemp(x, y);
    if (this.evictTo(x, y, existing, exTemp)) {
      this.set(x, y, mat, temp);
      return true;
    }
    return false;
  }

  /**
   * Find somewhere for a displaced cell to go: the nearest empty neighbour,
   * searched outward. Liquids find space easily; a fully packed volume falls
   * back to the particle hook so the matter still exists somewhere.
   */
  private evictTo(x: number, y: number, mat: MatId, temp: number): boolean {
    // Prefer straight up — displaced liquid rising is what reads correctly
    // when you drop a block into a pool. The radius has to be generous enough
    // to reach the surface from inside a body of liquid; 4 leaves anything
    // deeper than knee-height with nowhere to go.
    for (let r = 1; r <= 8; r++) {
      for (let k = 0; k < EVICT_ORDER.length; k += 2) {
        const nx = x + EVICT_ORDER[k] * r;
        const ny = y + EVICT_ORDER[k + 1] * r;
        if (this.peek(nx, ny) === VOID) {
          this.set(nx, ny, mat, temp);
          return true;
        }
      }
    }
    if (this.onEvict) {
      this.onEvict(x, y, mat, temp);
      return true;
    }
    return false;
  }

  /**
   * Write all four of a cell's words at once, exactly as given.
   *
   * The difference from `set` is that `set` is *authoritative about state*: it
   * resets flags to the variant bits and re-seeds `aux` from the material,
   * because a worldgen deposit or a transmutation is a new cell and should not
   * inherit the old one's burn timer. A rigid body's write-back is the opposite
   * — it is the *same* cell being put back down one tick later, and every word
   * has to survive the round trip. Losing `aux` there gives a burning crate a
   * full fuel tank every tick, so it burns forever and never chars (#42).
   *
   * No `place`/`evict` semantics and no density check: the caller has already
   * established the cell is free, which for the body layer means it lifted the
   * cell itself on the same tick.
   */
  writeRaw(x: number, y: number, mat: MatId, temp: number, flags: number, aux: number): void {
    const c = this.chunkFor(x, y);
    const lx = x & CHUNK_MASK;
    const ly = y & CHUNK_MASK;
    const i = (ly << CHUNK_SHIFT) | lx;

    const prev = c.mat[i];
    if (prev !== VOID) c.nonEmpty--;
    if (mat !== VOID) c.nonEmpty++;
    if (M.lightSource[prev] === 1) c.lightSources--;
    if (M.lightSource[mat] === 1) c.lightSources++;
    if (c.flags[i] & Flag.BURNING) c.burning--;
    if (flags & Flag.BURNING) c.burning++;

    c.mat[i] = mat;
    c.temp[i] = clampTemp(temp);
    c.flags[i] = flags;
    c.aux[i] = aux & 0xffff;

    c.touch(lx, ly);
    this.wakeNeighbours(c, lx, ly);
    this.revision++;
  }

  /**
   * Fired by `damageCell` after destruction lands.
   *
   * The rigid-body layer's only wake path for a sleeping body (#42). It is a
   * hook rather than a direct call because a sleeping body's cells are, to
   * every other system in the game, ordinary destructible matter — and that is
   * the property that makes sleeping free.
   */
  onCellDamaged: ((x: number, y: number) => void) | null = null;

  /** Change material while preserving temperature and flags. For reactions. */
  transmute(x: number, y: number, mat: MatId): void {
    const c = this.chunkFor(x, y);
    const lx = x & CHUNK_MASK;
    const ly = y & CHUNK_MASK;
    const i = (ly << CHUNK_SHIFT) | lx;

    const prev = c.mat[i];
    if (prev === mat) return;
    if (prev !== VOID) c.nonEmpty--;
    if (mat !== VOID) c.nonEmpty++;
    if (M.lightSource[prev] === 1) c.lightSources--;
    if (M.lightSource[mat] === 1) c.lightSources++;

    c.mat[i] = mat;
    c.aux[i] = initialAux(mat);
    // A transmuted cell is no longer frozen/burning by default — the reaction
    // that caused it re-applies whatever state it wants.
    if (c.flags[i] & Flag.BURNING) {
      c.burning--;
      c.flags[i] &= ~Flag.BURNING;
    }
    c.flags[i] &= ~(Flag.FROZEN | Flag.MOVED);

    c.touch(lx, ly);
    this.wakeNeighbours(c, lx, ly);
    this.revision++;
  }

  /**
   * Heat writes take the thermal-only touch: injecting heat schedules
   * conduction and a re-upload, but does not by itself make matter move. If the
   * heat causes a phase change, that goes through `transmute`/`set`, which do.
   */
  setTemp(x: number, y: number, t: number): void {
    const c = this.chunkFor(x, y);
    const lx = x & CHUNK_MASK;
    const ly = y & CHUNK_MASK;
    c.temp[(ly << CHUNK_SHIFT) | lx] = clampTemp(t);
    c.touchTemp(lx, ly);
  }

  addTemp(x: number, y: number, d: number): void {
    const c = this.chunkFor(x, y);
    const lx = x & CHUNK_MASK;
    const ly = y & CHUNK_MASK;
    const i = (ly << CHUNK_SHIFT) | lx;
    c.temp[i] = clampTemp(c.temp[i] + d);
    c.touchTemp(lx, ly);
    this.wakeNeighboursTemp(c, lx, ly);
  }

  setFlags(x: number, y: number, f: number): void {
    const c = this.chunkFor(x, y);
    const lx = x & CHUNK_MASK;
    const ly = y & CHUNK_MASK;
    const i = (ly << CHUNK_SHIFT) | lx;
    const was = c.flags[i] & Flag.BURNING;
    c.flags[i] = f;
    const now = f & Flag.BURNING;
    if (was && !now) c.burning--;
    else if (!was && now) c.burning++;
    c.touch(lx, ly);
  }

  addFlag(x: number, y: number, f: number): void {
    const c = this.chunkFor(x, y);
    const lx = x & CHUNK_MASK;
    const ly = y & CHUNK_MASK;
    const i = (ly << CHUNK_SHIFT) | lx;
    if (f & Flag.BURNING && !(c.flags[i] & Flag.BURNING)) c.burning++;
    c.flags[i] |= f;
    c.touch(lx, ly);
  }

  clearFlag(x: number, y: number, f: number): void {
    const c = this.chunkFor(x, y);
    const lx = x & CHUNK_MASK;
    const ly = y & CHUNK_MASK;
    const i = (ly << CHUNK_SHIFT) | lx;
    if (f & Flag.BURNING && c.flags[i] & Flag.BURNING) c.burning--;
    c.flags[i] &= ~f;
    c.touch(lx, ly);
  }

  setAux(x: number, y: number, v: number): void {
    const c = this.chunkFor(x, y);
    c.aux[((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK)] = v & 0xffff;
  }

  /** Swap two cells wholesale. The core primitive of the falling-sand step. */
  swap(x1: number, y1: number, x2: number, y2: number): void {
    const c1 = this.chunkFor(x1, y1);
    const i1 = ((y1 & CHUNK_MASK) << CHUNK_SHIFT) | (x1 & CHUNK_MASK);
    const c2 = this.chunkFor(x2, y2);
    const i2 = ((y2 & CHUNK_MASK) << CHUNK_SHIFT) | (x2 & CHUNK_MASK);

    const m = c1.mat[i1];
    const t = c1.temp[i1];
    const f = c1.flags[i1];
    const a = c1.aux[i1];
    c1.mat[i1] = c2.mat[i2];
    c1.temp[i1] = c2.temp[i2];
    c1.flags[i1] = c2.flags[i2];
    c1.aux[i1] = c2.aux[i2];
    c2.mat[i2] = m;
    c2.temp[i2] = t;
    c2.flags[i2] = f;
    c2.aux[i2] = a;

    if (c1 !== c2) {
      // Counts move with the cells when the swap crosses a chunk boundary.
      const m1Empty = c1.mat[i1] === VOID;
      const m2Empty = c2.mat[i2] === VOID;
      if (m1Empty !== m2Empty) {
        if (m1Empty) {
          c1.nonEmpty--;
          c2.nonEmpty++;
        } else {
          c1.nonEmpty++;
          c2.nonEmpty--;
        }
      }
      const s1 = M.lightSource[c1.mat[i1]] === 1;
      const s2 = M.lightSource[c2.mat[i2]] === 1;
      if (s1 !== s2) {
        if (s1) {
          c1.lightSources++;
          c2.lightSources--;
        } else {
          c1.lightSources--;
          c2.lightSources++;
        }
      }
      const b1 = (c1.flags[i1] & Flag.BURNING) !== 0;
      const b2 = (c2.flags[i2] & Flag.BURNING) !== 0;
      if (b1 !== b2) {
        if (b1) {
          c1.burning++;
          c2.burning--;
        } else {
          c1.burning--;
          c2.burning++;
        }
      }
    }

    const lx1 = x1 & CHUNK_MASK;
    const ly1 = y1 & CHUNK_MASK;
    const lx2 = x2 & CHUNK_MASK;
    const ly2 = y2 & CHUNK_MASK;
    c1.touch(lx1, ly1);
    c2.touch(lx2, ly2);
    this.wakeNeighbours(c1, lx1, ly1);
    this.wakeNeighbours(c2, lx2, ly2);
    this.revision++;
  }

  /**
   * When a cell on a chunk edge changes, the neighbouring chunk must wake —
   * otherwise sand pours off the edge of a sleeping chunk and freezes midair.
   */
  private wakeNeighbours(c: Chunk, lx: number, ly: number): void {
    if (lx === 0) this.wakeAt(c.cx - 1, c.cy, CHUNK_MASK, ly);
    else if (lx === CHUNK_MASK) this.wakeAt(c.cx + 1, c.cy, 0, ly);
    if (ly === 0) this.wakeAt(c.cx, c.cy - 1, lx, CHUNK_MASK);
    else if (ly === CHUNK_MASK) this.wakeAt(c.cx, c.cy + 1, lx, 0);
  }

  private wakeAt(cx: number, cy: number, lx: number, ly: number): void {
    const n = this.chunks.get(chunkKey(cx, cy));
    if (n !== undefined) n.touch(lx, ly);
  }

  /**
   * Thermal-only form of `wakeNeighbours`, so heat still crosses chunk seams.
   *
   * Public because `thermal.react` has to call it directly: a temperature that
   * changes on a chunk edge must put the *neighbouring* chunk into next tick's
   * thermal rect, or the conduction frontier stops dead at the seam and fires
   * stall on a 128-cell grid. This is the invariant the old whole-chunk
   * widening was papering over.
   */
  wakeNeighboursTemp(c: Chunk, lx: number, ly: number): void {
    if (lx === 0) this.wakeTempAt(c.cx - 1, c.cy, CHUNK_MASK, ly);
    else if (lx === CHUNK_MASK) this.wakeTempAt(c.cx + 1, c.cy, 0, ly);
    if (ly === 0) this.wakeTempAt(c.cx, c.cy - 1, lx, CHUNK_MASK);
    else if (ly === CHUNK_MASK) this.wakeTempAt(c.cx, c.cy + 1, lx, 0);
  }

  private wakeTempAt(cx: number, cy: number, lx: number, ly: number): void {
    const n = this.chunks.get(chunkKey(cx, cy));
    if (n !== undefined) n.touchTemp(lx, ly);
  }

  // -------------------------------------------------------- convenience

  isSolid(x: number, y: number): boolean {
    const c = this.peekFor(x, y);
    if (c === null) return false;
    const i = ((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK);
    if (c.flags[i] & Flag.PHASED) return false;
    return M.solid[c.mat[i]] === 1;
  }

  isEmpty(x: number, y: number): boolean {
    return this.peek(x, y) === VOID;
  }

  /** True if this cell is a one-way (semi-solid) surface. */
  isOneWay(x: number, y: number): boolean {
    const c = this.peekFor(x, y);
    if (c === null) return false;
    const m = c.mat[((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK)];
    return m !== VOID && M.oneWay[m] !== 0;
  }

  /** Return codes for `probe`. */
  static readonly PROBE_BLOCKED = 0;
  static readonly PROBE_DISPLACE = 1;
  static readonly PROBE_ONE_WAY = 2;

  /**
   * Fused `isOneWay` + `canDisplace` for a single cell.
   *
   * The cell sim asks both questions about the cell directly below every loose
   * and liquid cell it steps, and asking them separately costs two chunk
   * lookups and two material reads for a case that almost never fires: in a
   * world with zero one-way cells the separate `isOneWay` probe measured
   * +0.212 ms/tick, 6.4% of the whole sim, for nothing. One lookup answers both.
   */
  probe(x: number, y: number, density: number): number {
    const c = this.peekFor(x, y);
    if (c === null) return CellWorld.PROBE_BLOCKED;
    const i = ((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK);
    const m = c.mat[i];
    if (m === VOID) return CellWorld.PROBE_DISPLACE;
    if (M.solid[m] === 1) return CellWorld.PROBE_BLOCKED;
    if (M.oneWay[m] !== 0) return CellWorld.PROBE_ONE_WAY;
    return density > M.density[m] * (1 + M.viscosity[m] * 3)
      ? CellWorld.PROBE_DISPLACE
      : CellWorld.PROBE_BLOCKED;
  }

  /** True if the cell can be displaced by something of the given density. */
  canDisplace(x: number, y: number, density: number): boolean {
    const c = this.peekFor(x, y);
    if (c === null) return false;
    const i = ((y & CHUNK_MASK) << CHUNK_SHIFT) | (x & CHUNK_MASK);
    const m = c.mat[i];
    if (m === VOID) return true;
    if (M.solid[m] === 1) return false;
    // A semi-solid is not solid to the cell sim (that is what makes it a
    // platform rather than a wall), but it must not be *swapped with* either:
    // a swap lifts the platform cell into the falling matter's place and the
    // catwalk visibly dents upward as sand pours over it. Matter passes
    // through instead — see `passThrough` in cellsim.
    if (M.oneWay[m] !== 0) return false;
    // Denser things sink through less-dense ones, resisted by viscosity.
    return density > M.density[m] * (1 + M.viscosity[m] * 3);
  }

  /**
   * Destroy a cell if it is soft enough. Returns true if something was removed.
   * The single entry point for all weapon/ability terrain destruction, so
   * hardness gating lives in exactly one place.
   */
  damageCell(x: number, y: number, power: number): boolean {
    const m = this.peek(x, y);
    if (m === VOID) return false;
    if (M.indestructible[m] === 1) return false;
    if (M.hardness[m] > power) return false;
    const shattered = M.shatteredInto[m];
    if (shattered >= 0 && M.brittleness[m] > 0.5) {
      this.transmute(x, y, shattered);
    } else {
      this.set(x, y, VOID);
    }
    if (this.onCellDamaged !== null) this.onCellDamaged(x, y);
    return true;
  }

  /** Circular destruction. Returns cells removed. */
  damageCircle(cx: number, cy: number, radius: number, power: number): number {
    const r2 = radius * radius;
    let n = 0;
    for (let y = Math.floor(cy - radius); y <= cy + radius; y++) {
      for (let x = Math.floor(cx - radius); x <= cx + radius; x++) {
        const dx = x - cx;
        const dy = y - cy;
        if (dx * dx + dy * dy > r2) continue;
        if (this.damageCell(x, y, power)) n++;
      }
    }
    return n;
  }

  /** Inject heat in a radius, falling off linearly. */
  heatCircle(cx: number, cy: number, radius: number, amount: number): void {
    for (let y = Math.floor(cy - radius); y <= cy + radius; y++) {
      for (let x = Math.floor(cx - radius); x <= cx + radius; x++) {
        const dx = x - cx;
        const dy = y - cy;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d > radius) continue;
        this.addTemp(x, y, amount * (1 - d / radius));
      }
    }
  }

  /** Fill a rect. Used by worldgen and by the debug painter. */
  fillRect(x0: number, y0: number, w: number, h: number, mat: MatId): void {
    for (let y = y0; y < y0 + h; y++) {
      for (let x = x0; x < x0 + w; x++) this.set(x, y, mat);
    }
  }

  fillCircle(cx: number, cy: number, r: number, mat: MatId): void {
    const r2 = r * r;
    for (let y = Math.floor(cy - r); y <= cy + r; y++) {
      for (let x = Math.floor(cx - r); x <= cx + r; x++) {
        const dx = x - cx;
        const dy = y - cy;
        if (dx * dx + dy * dy <= r2) this.set(x, y, mat);
      }
    }
  }

  /**
   * Raycast through cells (DDA). Calls `hit` for each cell along the ray until
   * it returns true or `maxDist` is exceeded. Returns the stopping distance,
   * or -1 if nothing stopped it.
   */
  raycast(
    x0: number,
    y0: number,
    dx: number,
    dy: number,
    maxDist: number,
    hit: (x: number, y: number, mat: MatId, dist: number) => boolean,
  ): number {
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return -1;
    const nx = dx / len;
    const ny = dy / len;
    let x = Math.floor(x0);
    let y = Math.floor(y0);
    const stepX = nx > 0 ? 1 : -1;
    const stepY = ny > 0 ? 1 : -1;
    const tDeltaX = nx === 0 ? Infinity : Math.abs(1 / nx);
    const tDeltaY = ny === 0 ? Infinity : Math.abs(1 / ny);
    let tMaxX =
      nx === 0 ? Infinity : (nx > 0 ? x + 1 - x0 : x0 - x) * tDeltaX;
    let tMaxY =
      ny === 0 ? Infinity : (ny > 0 ? y + 1 - y0 : y0 - y) * tDeltaY;
    let dist = 0;

    while (dist <= maxDist) {
      if (hit(x, y, this.peek(x, y), dist)) return dist;
      if (tMaxX < tMaxY) {
        dist = tMaxX;
        tMaxX += tDeltaX;
        x += stepX;
      } else {
        dist = tMaxY;
        tMaxY += tDeltaY;
        y += stepY;
      }
    }
    return -1;
  }

  // ------------------------------------------------------------- tiering

  /**
   * Rebuild `activeChunks` for this tick based on distance from the camera
   * focus. Chunks outside the frozen radius are dropped from memory entirely
   * (they regenerate deterministically from the seed).
   */
  updateActive(
    focusX: number,
    focusY: number,
    viewW: number,
    viewH: number,
    evictRadius = 12,
  ): void {
    const fcx = Math.floor(focusX) >> CHUNK_SHIFT;
    const fcy = Math.floor(focusY) >> CHUNK_SHIFT;
    const halfW = Math.ceil(viewW / 2 / CHUNK_SIZE);
    const halfH = Math.ceil(viewH / 2 / CHUNK_SIZE);

    this.activeChunks.length = 0;

    // Ensure chunks covering the view (plus one ring) exist.
    for (let cy = fcy - halfH - 1; cy <= fcy + halfH + 1; cy++) {
      for (let cx = fcx - halfW - 1; cx <= fcx + halfW + 1; cx++) {
        this.getChunk(cx, cy);
      }
    }

    const resident = this.resident;
    for (let k = 0; k < resident.length; k++) {
      const c = resident[k];
      const dxr = c.cx - fcx;
      const dyr = c.cy - fcy;
      const dx = dxr < 0 ? -dxr : dxr;
      const dy = dyr < 0 ? -dyr : dyr;
      const rx = dx - halfW;
      const ry = dy - halfH;
      const ring = rx > ry ? (rx > 0 ? rx : 0) : ry > 0 ? ry : 0;

      // `ring` is already distance *beyond* the visible chunk span, so ring 0
      // is exactly the on-screen set. Treating ring<=1 as HOT quadrupled the
      // hot working set for no visible benefit.
      let tier: TierId;
      if (ring === 0) tier = Tier.HOT;
      else if (ring <= 2) tier = Tier.WARM;
      else if (ring <= 4) tier = Tier.COLD;
      else tier = Tier.FROZEN;

      const wasFrozen = c.tier === Tier.FROZEN;
      c.tier = tier;

      if (tier === Tier.FROZEN) continue;

      // A chunk returning from frozen gets a catch-up pass so the world
      // doesn't lie about what happened while you were away.
      if (wasFrozen && this.tick - c.lastSimTick > 60) {
        this.catchUp(c, this.tick - c.lastSimTick);
      }

      const rate = tier === Tier.HOT ? 1 : tier === Tier.WARM ? 3 : 15;
      if (this.tick % rate !== 0) continue;
      if (c.asleep && !c.hasDirty() && !c.hasThermDirty()) continue;

      this.activeChunks.push(c);
    }

    if (this.chunks.size > 256) this.evictDistant(fcx, fcy, evictRadius);
  }

  /** Compact `resident` in place while dropping chunks past the evict radius. */
  private evictDistant(fcx: number, fcy: number, radius: number): void {
    const resident = this.resident;
    let w = 0;
    for (let k = 0; k < resident.length; k++) {
      const c = resident[k];
      if (Math.abs(c.cx - fcx) > radius || Math.abs(c.cy - fcy) > radius) {
        const key = chunkKey(c.cx, c.cy);
        this.chunks.delete(key);
        if (this.cacheKey === key) {
          this.cacheKey = -1;
          this.cacheChunk = null;
        }
        continue;
      }
      resident[w++] = c;
    }
    resident.length = w;
  }

  /**
   * Coarse model of elapsed time in a chunk that was frozen. Cheap and
   * plausible: fires burn out, heat relaxes toward ambient, and burning cells
   * become their ash product. Runs in O(cells) once, not per elapsed tick.
   */
  private catchUp(c: Chunk, elapsedTicks: number): void {
    const relax = Math.min(1, elapsedTicks / 600);
    for (let i = 0; i < CELLS_PER_CHUNK; i++) {
      const m = c.mat[i];
      if (m === VOID) continue;

      if (c.flags[i] & Flag.BURNING) {
        const fuel = c.aux[i];
        if (fuel <= elapsedTicks) {
          const ash = M.ashesInto[m];
          c.flags[i] &= ~Flag.BURNING;
          c.burning--;
          if (M.lightSource[m] === 1) c.lightSources--;
          if (ash >= 0) {
            if (M.lightSource[ash] === 1) c.lightSources++;
            c.mat[i] = ash;
            c.aux[i] = M.burnDuration[ash];
          } else {
            if (c.mat[i] !== VOID) c.nonEmpty--;
            c.mat[i] = VOID;
          }
        } else {
          c.aux[i] = fuel - elapsedTicks;
        }
      }

      const ambient = 20;
      c.temp[i] = clampTemp(c.temp[i] + (ambient - c.temp[i]) * relax);
    }
    c.lastSimTick = this.tick;
    c.touchAll();
  }

  /** Total cells with content, across resident chunks. Debug/telemetry. */
  countNonEmpty(): number {
    let n = 0;
    for (let k = 0; k < this.resident.length; k++) n += this.resident[k].nonEmpty;
    return n;
  }

  /**
   * Burning cells across resident chunks.
   *
   * Exact, not memoised — tests and the ticker read it mid-tick and a cached
   * answer would be a lie about the world. It is cheap enough now that it walks
   * the dense resident array instead of allocating a Map iterator, but it is
   * still O(chunks) and callers must treat it as telemetry: `thermal.step`
   * reports it only when the profiler is actually recording.
   */
  countBurning(): number {
    let n = 0;
    for (let k = 0; k < this.resident.length; k++) n += this.resident[k].burning;
    return n;
  }

  clear(): void {
    this.chunks.clear();
    this.resident.length = 0;
    this.cacheKey = -1;
    this.cacheChunk = null;
    this.activeChunks.length = 0;
    this.revision++;
  }
}

/**
 * Eviction search order, as (dx, dy) pairs. Up first: displaced liquid rising
 * is what reads correctly when you drop matter into a pool. Sideways next,
 * down last — pushing liquid downward looks like it fell through the floor.
 */
const EVICT_ORDER = [0, -1, -1, 0, 1, 0, -1, -1, 1, -1, 0, 1, -1, 1, 1, 1];

/**
 * The starting value of a cell's `aux` slot.
 *
 * `aux` is one 16-bit word doing two jobs, and which job depends on the
 * material: `cellsim` reads it as an **age** counter for anything with a
 * `lifetime`, and `thermal` reads it as remaining **fuel** for anything that
 * burns. Seeding it with `burnDuration` unconditionally therefore handed every
 * transient cell a head start on its own death — flash vapour was born 120 of
 * its 180 ticks old, and lightning (lifetime 3, burnDuration 3) was born
 * expired and vanished on the tick it appeared.
 *
 * A material with a lifetime starts at age zero; everything else starts with a
 * full tank. `METHANE` and `SPORE_CLOUD` legitimately declare both fields and
 * are the reason this cannot be fixed by zeroing `burnDuration` in the material
 * table instead.
 */
function initialAux(mat: MatId): number {
  return M.lifetime[mat] > 0 ? 0 : M.burnDuration[mat];
}

export function clampTemp(t: number): number {
  return t < -273 ? -273 : t > 32000 ? 32000 : t | 0;
}

const defaultGenerator: ChunkGenerator = (chunk) => {
  chunk.mat.fill(VOID);
  chunk.temp.fill(20);
};
